/**
 * Security Dashboard — Phase G.4 (Super Admin only).
 * Small aggregate view over locked accounts, recent resets, failed logins,
 * active sessions and recently suspended team members.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, KeyRound, AlertCircle, Users, Clock, RefreshCw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Card, Badge, Skeleton } from "@/components/primitives";
import { Button } from "@/components/ui/button";

function StatTile({ icon: Icon, label, value, tone = "neutral", testid }) {
  return (
    <Card padded testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center
                          ${tone === "error" ? "bg-hocp-error/10 text-hocp-error"
                            : tone === "warning" ? "bg-hocp-vanilla/40 text-hocp-textPrimary"
                            : "bg-hocp-alice text-hocp-textPrimary"}`}>
          <Icon className="w-5 h-5" strokeWidth={1.75}/>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-hocp-textTertiary">{label}</div>
          <div className="text-2xl font-semibold text-hocp-textPrimary tabular-nums">{value}</div>
        </div>
      </div>
    </Card>
  );
}

export default function SecurityDashboard() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/security/dashboard");
      setD(data);
    } catch (e) { toast.error(formatApiError(e) || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading && !d) {
    return (
      <div className="space-y-6">
        <PageHeader title="Security" subtitle="Locked accounts · recent resets · active sessions."/>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24"/>)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="security-dashboard-page">
      <PageHeader
        title="Security"
        subtitle="Locked accounts · recent resets · active sessions."
        action={
          <Button variant="outline" size="sm" onClick={load} data-testid="security-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1"/> Refresh
          </Button>
        }
        testid="page-security"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile icon={ShieldAlert} label="Locked Accounts" value={d.locked_accounts_count}
                   tone={d.locked_accounts_count > 0 ? "error" : "neutral"} testid="stat-locked"/>
        <StatTile icon={KeyRound}    label="Resets (24h)" value={d.recent_password_resets?.length || 0}
                   tone="warning" testid="stat-resets"/>
        <StatTile icon={AlertCircle} label="Failed Logins (24h)" value={d.failed_login_attempts_24h}
                   tone={d.failed_login_attempts_24h > 10 ? "error" : "neutral"} testid="stat-failed"/>
        <StatTile icon={Users}       label="Active Sessions" value={d.active_sessions_count}
                   testid="stat-sessions"/>
      </div>

      {/* Locked accounts */}
      <Card testid="locked-accounts-card">
        <div className="p-5 border-b border-hocp-border">
          <div className="font-display font-semibold text-[16px] text-hocp-textPrimary">Locked Accounts</div>
          <div className="text-[12px] text-hocp-textSecondary">Open a team member's profile to unlock.</div>
        </div>
        <div className="divide-y divide-hocp-alice/60">
          {d.locked_accounts?.length === 0 && (
            <div className="p-5 text-center text-[13px] text-hocp-textSecondary">No locked accounts.</div>
          )}
          {d.locked_accounts?.map(u => (
            <div key={u.user_id} className="p-4 flex items-center justify-between gap-3"
                  data-testid={`locked-${u.user_id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-hocp-textPrimary">{u.name || u.email}</div>
                <div className="text-[11px] text-hocp-textTertiary tabular-nums">
                  Locked {u.locked_at?.slice(0,19).replace("T"," ")} · {u.failed_login_count} failed attempts
                </div>
                {u.lock_reason && <div className="text-[11px] italic text-hocp-textSecondary mt-0.5">{u.lock_reason}</div>}
              </div>
              <Link to="/team" className="text-[12px] font-semibold text-hocp-textPrimary hover:underline">
                Open in Team Members →
              </Link>
            </div>
          ))}
        </div>
      </Card>

      {/* Recent resets */}
      <Card testid="recent-resets-card">
        <div className="p-5 border-b border-hocp-border">
          <div className="font-display font-semibold text-[16px] text-hocp-textPrimary">Recent Password Resets (24h)</div>
        </div>
        <div className="divide-y divide-hocp-alice/60">
          {d.recent_password_resets?.length === 0 && (
            <div className="p-5 text-center text-[13px] text-hocp-textSecondary">No resets in the last 24 hours.</div>
          )}
          {d.recent_password_resets?.map((r, i) => (
            <div key={i} className="p-4 flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-hocp-textPrimary">{r.email || "—"}</div>
                <div className="text-[11px] text-hocp-textTertiary tabular-nums">
                  {r.timestamp?.slice(0,19).replace("T"," ")} · by {r.actor_email}
                </div>
                {r.reason && <div className="text-[11px] italic text-hocp-textSecondary mt-0.5">{r.reason}</div>}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Recently suspended */}
      <Card testid="recently-suspended-card">
        <div className="p-5 border-b border-hocp-border">
          <div className="font-display font-semibold text-[16px] text-hocp-textPrimary">Recently Suspended</div>
        </div>
        <div className="divide-y divide-hocp-alice/60">
          {d.recently_suspended?.length === 0 && (
            <div className="p-5 text-center text-[13px] text-hocp-textSecondary">No suspended team members.</div>
          )}
          {d.recently_suspended?.map(u => (
            <div key={u.user_id} className="p-4 flex items-center justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-semibold text-hocp-textPrimary">{u.name || u.email}</div>
                <div className="text-[11px] text-hocp-textTertiary tabular-nums">
                  {u.suspended_at?.slice(0,19).replace("T"," ")}
                </div>
              </div>
              <Badge tone="warning" size="sm">Suspended</Badge>
            </div>
          ))}
        </div>
      </Card>

      <div className="text-[11px] text-hocp-textTertiary text-right tabular-nums">
        Generated {d.generated_at?.slice(0,19).replace("T"," ")}
      </div>
    </div>
  );
}
